import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CubeTransparentIcon,
  ArrowPathIcon,
  BeakerIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  RocketLaunchIcon,
  BuildingLibraryIcon,
  WindowIcon,
} from '@heroicons/react/24/outline';
import TargetAudienceBadge from 'components/TargetAudienceBadge';
import NotAvailableFlowButton from 'components/NotAvailableFlowButton';
import { persistor } from 'app/store';
import { classNames } from 'utils/styleUtils';
import { sendMetrics, MetricType } from 'utils/metrics';
import { selectEmail } from 'features/authentication/reducers/userSlice';
import { useAppSelector } from 'app/hooks';

interface IFlow {
  name: string;
  type: MetricType;
  description: string;
  targetAudience: string[];
  disabled: boolean;
  hidden?: boolean;
  link: string;
  icon: React.ElementType;
}

export default function ChooseFlow(): JSX.Element {
  const navigate = useNavigate();
  const email = useAppSelector(selectEmail);
  const [flows, setFlows] = useState<IFlow[]>([]);

  const deploymentEnv = process.env.REACT_APP_ENV;

  const showAZFlow = useMemo(
    () =>
      (deploymentEnv === 'dev' || deploymentEnv === 'qa' || deploymentEnv === 'qa-local' || deploymentEnv === 'az') &&
      (email.endsWith('@astrazeneca.com') ||
        email.endsWith('@dataops.live') ||
        email.endsWith('@imaginarycloud.com') ||
        email.endsWith('@alexion.com')),
    [email, deploymentEnv],
  );

  useEffect(() => {
    setFlows([
      {
        name: 'Dynamic Delivery',
        type: MetricType.create_dynamic_delivery,
        description:
          'Dynamic Delivery from DataOps.Live delivers an enterprise Snowflake CI/CD solution that automates the deployment and version control of Snowflake objects, streamlining schema management with zero manual setup. It accelerates time-to-value while ensuring operational efficiency across Snowflake environments.',
        targetAudience: ['Data Operations', 'Data Engineering'],
        disabled: false,
        hidden: false,
        link: '/dynamic-delivery',
        icon: RocketLaunchIcon,
      },
      {
        name: 'Dynamic Transformation',
        type: MetricType.create_dbt,
        description:
          'Dynamic Transformation from DataOps.live gives your dbt™ workflows a production-grade foundation. It adds automation, governance, and testing to help you run standardized processes in minutes — not days — natively inside Snowflake.',
        targetAudience: ['Data Engineer', 'DataOps Developer'],
        disabled: false,
        link: '/dbt-migration',
        icon: ArrowPathIcon,
      },
      {
        name: 'AstraZeneca Flow',
        type: MetricType.create_az,
        description:
          'You can connect your snowflake account or bring your own SQL to create your first end to end data product in minutes. Application will guide you through all the steps starting from defining your data product, configuring your snowflake account, confirming auto generated tests and working with your personal AI Assistant to help you transform your data and publish your data product.',
        targetAudience: ['Data Product Owner', 'Data Analyst'],
        disabled: false,
        hidden: !showAZFlow,
        link: '/astra-zeneca-flow',
        icon: CubeTransparentIcon,
      },
      {
        name: 'AstraZeneca Monitoring',
        type: MetricType.create_az_monitoring,
        description: 'Monitor the status of the pipelines and merge requests in your Data Products.',
        targetAudience: ['Data Product Owner', 'Data Analyst'],
        disabled: false,
        hidden: !showAZFlow,
        link: '/astra-zeneca-monitoring',
        icon: ChartBarIcon,
      },
      {
        name: 'Snowflake Native App',
        type: MetricType.create_native_app,
        description:
          'Create and deploy a Snowflake Native App in minutes. Everything from sql to Snowpark, Streamlit to Cortex and even Container Services - automatically build, test and deploy Native Apps at the click of a button, including test subscriptions.',
        targetAudience: ['Snowflake Developer', 'DataOps Developer', 'Data Scientist', 'Native App Developer'],
        disabled: false,
        link: '/native-app-builder',
        icon: WindowIcon,
      },
      {
        name: 'Data App Builder',
        type: MetricType.create_native_app,
        description:
          'Create and deploy Snowflake components in minutes. Everything from sql to Snowpark, Streamlit to Cortex and even Container Services - automatically build, test and deploy components at the click of a button, including test subscriptions.',
        targetAudience: ['Snowflake Developer', 'DataOps Developer', 'Data Scientist'],
        disabled: false,
        hidden: deploymentEnv === 'production' || deploymentEnv === 'az',
        link: '/data-app-builder',
        icon: BeakerIcon,
      },
      {
        name: 'Snowflake Solution Central',
        type: MetricType.create_native_app,
        description:
          'Create Snowflake Solution Central project in a few clicks. This can be to either create new SSC Solutions or import an existing SSC Solution that you have created outside of Snowflake Solution Central.',
        targetAudience: ['SSC Producer'],
        disabled: false,
        hidden: deploymentEnv === 'production' || deploymentEnv === 'az',
        link: '/snowflake-solution-central',
        icon: BuildingLibraryIcon,
      },
      {
        name: 'Streamlit Data Product',
        type: MetricType.create_streamlit,
        description:
          'Streamline your journey to production with our guided flow, simplifying the creation of your first DataOps pipeline for Streamlit data applications. Perfect for Snowflake users aiming to enhance data accessibility and insights without extensive web development experience.',
        targetAudience: ['Snowflake Developer', 'DataOps Developer'],
        disabled: true,
        hidden: deploymentEnv === 'production' || deploymentEnv === 'az',
        link: '/',
        icon: WindowIcon,
      },
      {
        name: 'Analytical Data Product',
        type: MetricType.create_analytics,
        description:
          'You can connect your snowflake account or bring your own SQL to create your first end to end data product in minutes. Application will guide you through all the steps starting from defining your data product, configuring your snowflake account, confirming auto generated tests and working with your personal AI Assistant to help you transform your data and publish your data product.',
        targetAudience: ['Data Product Owner', 'Data Analyst'],
        disabled: false,
        link: '/data-product-creator',
        icon: PresentationChartLineIcon,
      },
    ]);
  }, [showAZFlow]);

  return (
    <div className="my-4 mx-14 pb-6 grid grid-cols-1 sm:grid-cols-2 wide:grid-cols-3 gap-4 mt-[4.9rem]">
      {flows
        .filter((flow) => flow.hidden !== true)
        .map((flow) => (
          <div
            key={flow.name}
            className={classNames(
              flow.disabled ? 'bg-gray-50' : 'bg-white',
              'relative p-6 sm:p-8 flex flex-col items-start',
            )}
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-50 mr-2">
                <flow.icon className="h-6 w-6 text-dataops-primary" aria-hidden="true" />
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-gray-900">{flow.name}</h3>
            </div>
            <div className="mt-2 flex flex-wrap">
              {flow.targetAudience.map((audience) => (
                <TargetAudienceBadge key={audience} label={audience} />
              ))}
            </div>
            <p className="mt-4 mb-8 text-base leading-7 text-gray-600">{flow.description}</p>
            {!flow.disabled && (
              <button
                className="absolute bottom-6 right-8 min-w-[8rem] rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400"
                onClick={() => {
                  sendMetrics(flow.type, '0');
                  persistor
                    .purge()
                    .then((res) => {
                      navigate(flow.link);
                      console.log('purge: ', res);
                    })
                    .catch((err) => {
                      console.log('purge error: ', err);
                    });
                }}
              >
                Select
              </button>
            )}
            {flow.disabled && <NotAvailableFlowButton />}
          </div>
        ))}
    </div>
  );
}
